
import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { useNavigate } from "react-router-dom"

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
}

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.3,
    },
  },
}

const pulseVariants = {
  animate: {
    opacity: [1, 0.7, 1],
    transition: {
      duration: 2,
      repeat: Number.POSITIVE_INFINITY,
    },
  },
}

export default function AccountOverview() {
  const [timeLeft, setTimeLeft] = useState("10:06:39")
  const [refreshTime, setRefreshTime] = useState("04:43")
  const navigate = useNavigate()
  // Countdown timer
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        const parts = prev.split(":").map(Number)
        let hours = parts[0]
        let minutes = parts[1]
        let seconds = parts[2]

        if (seconds > 0) {
          seconds--
        } else if (minutes > 0) {
          seconds = 59
          minutes--
        } else if (hours > 0) {
          minutes = 59
          seconds = 59
          hours--
        }

        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  const stats = [
    { value: "$50,000", label: "Balance", icon: "💰", bgColor: "bg-purple-100" },
    { value: "$8,500", label: "Profit/Loss", icon: "📈", bgColor: "bg-cyan-100" },
    { value: "$0", label: "Floating Loss", icon: "📊", bgColor: "bg-blue-100" },
    { value: "10", label: "Trading Days", icon: "📅", bgColor: "bg-gray-100" },
  ]

  const objectives = [
    {
      title: "Daily Loss Limit",
      status: "Ongoing",
      statusColor: "bg-purple-100 text-purple-700",
      details: [
        { label: "Max Daily Limit", value: "1,250.00" },
        { label: "Daily Loss till now", value: "$ 0" },
        { label: "Today's Permitted Loss", value: "$2,500" },
      ],
      icon: "📍",
    },
    {
      title: "Maximum Loss Limit",
      status: "Ongoing",
      statusColor: "bg-purple-100 text-purple-700",
      details: [
        { label: "Max Loss Limit", value: "2,500.00" },
        { label: "Max Loss till now", value: "$ 0" },
        { label: "Max Permitted Loss", value: "$5,000" },
      ],
      icon: "📌",
    },
    {
      title: "Minimum Trading Days",
      status: "Passed",
      statusColor: "bg-green-100 text-green-700",
      details: [
        { label: "Minimum", value: "10 Days" },
        { label: "Current Result", value: "5" },
      ],
      icon: "✓",
    },
    {
      title: "Profit Target",
      status: "Passed",
      statusColor: "bg-green-100 text-green-700",
      details: [
        { label: "Minimum", value: "6,250.00" },
        { label: "Current Result", value: "$12,500" },
      ],
      icon: "🎯",
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50 shadow-lg">
      <motion.div
        className="w-full  mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 shadow-lg"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header Section */}
        <motion.div
          className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8"
          variants={itemVariants}
          onClick={()=>navigate("/accounts/trading-overview")}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6  md:divide-x md:divide-gray-300" >
            {/* User Info */}
            <motion.div className="md:col-span-1  p-4" variants={itemVariants}>
              <div className="flex items-center gap-4">
                <motion.div className="w-12 h-12 rounded-full bg-gray-300" whileHover={{ scale: 1.1 }} />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Hello Aman Dwivedi !</h2>
                  <p className="text-sm text-gray-600">Currently, you have an Evaluation account</p>
                </div>
              </div>
              <motion.div className="mt-4 space-y-2 text-sm p-4">
                <motion.p className="text-gray-700 flex items-center gap-2" whileHover={{ x: 5 }}>
                  <span>✓</span>
                  <span>Initial Balance: $50,000</span>
                </motion.p>
                <motion.p className="text-gray-700 flex items-center gap-2" whileHover={{ x: 5 }}>
                  <span>✓</span>
                  <span>Plan Type: Express Demo|50k</span>
                </motion.p>
                <motion.p className="text-gray-700 flex items-center gap-2" whileHover={{ x: 5 }}>
                  <span>✓</span>
                  <span>Account Type: Swap</span>
                </motion.p>
              </motion.div>
            </motion.div>

            {/* Account Details */}
            <motion.div className="md:col-span-1 p-4" variants={itemVariants}>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span>📍</span>
                Account Details
                <motion.span
                  className="text-gray-400 text-lg cursor-help"
                  whileHover={{ rotate: 20, scale: 1.2 }}
                  transition={{ duration: 0.3 }}
                >
                  ⓘ
                </motion.span>
              </h3>
              <div className="space-y-3 text-sm">
  {/* Login */}
  <div className="grid grid-cols-[1fr_auto_auto] items-center p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
    <span className="text-gray-600">Login</span>
    <motion.span
      className="font-mono font-semibold text-gray-900 cursor-pointer text-right"
      whileHover={{ scale: 1.05 }}
    >
      123456
    </motion.span>
    <motion.button
      className="text-gray-400 hover:text-gray-600 ml-2"
      whileHover={{ scale: 1.2 }}
      whileTap={{ scale: 0.9 }}
    >
      📋
    </motion.button>
  </div>

  {/* Investor Password */}
  <div className="grid grid-cols-[1fr_auto_auto] items-center p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
    <span className="text-gray-600">Investor Pass.</span>
    <motion.span
      className="font-mono font-semibold text-gray-900 cursor-pointer text-right"
      whileHover={{ scale: 1.05 }}
    >
      @Fn2022*
    </motion.span>
    <motion.button
      className="text-gray-400 hover:text-gray-600 ml-2"
      whileHover={{ scale: 1.2 }}
      whileTap={{ scale: 0.9 }}
    >
      📋
    </motion.button>
  </div>

  {/* Master Password */}
  <div className="grid grid-cols-[1fr_auto_auto] items-center p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
    <span className="text-gray-600">Master Pass.</span>
    <motion.span className="font-semibold text-gray-900 text-right">••••</motion.span>
    <div className="flex gap-2 ml-2">
      <motion.button
        className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Reset
      </motion.button>
      <motion.button
        className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        View
      </motion.button>
    </div>
  </div>

  {/* Server */}
  <div className="grid grid-cols-[1fr_auto] items-center p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors">
    <span className="text-gray-600">Server</span>
    <motion.span className="font-semibold text-gray-900 text-right">
      FundedNext-Server
    </motion.span>
  </div>
</div>

            </motion.div>

            {/* Trading Cycle Details */}
            <motion.div className="md:col-span-1 p-4" variants={itemVariants}>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span>🕐</span>
                Trading Cycle Details
              </h3>
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-600">Start Date</p>
                  <motion.p className="font-semibold text-gray-900" whileHover={{ scale: 1.05 }}>
                    Dec 5, 2022
                  </motion.p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-600">End Date</p>
                  <motion.p className="font-semibold text-gray-900" whileHover={{ scale: 1.05 }}>
                    Dec 31, 2022
                  </motion.p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Stats Section */}
        <motion.div variants={itemVariants} className="mb-6 sm:mb-8">
          <div className="flex items-center  gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">📊 Stats</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                className={`${stat.bgColor} rounded-lg p-4 cursor-pointer`}
                variants={cardVariants}
                whileHover="hover"
              >
                <motion.div className="text-2xl sm:text-3xl mb-2" whileHover={{ rotate: 10, scale: 1.1 }}>
                  {stat.icon}
                </motion.div>
                <motion.p className="text-xl sm:text-2xl font-bold text-gray-900" whileHover={{ scale: 1.05 }}>
                  {stat.value}
                </motion.p>
                <p className="text-xs sm:text-sm text-gray-700 mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Left Section - Objectives */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                🎯 Trading Objective
                <motion.span
                  className="text-sm text-purple-600 cursor-help"
                //   whileHover={{ rotate: 360 }}
                  transition={{ duration: 1 }}
                >
                  (Refreshing in {refreshTime})
                </motion.span>
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {objectives.map((objective, idx) => (
                <motion.div
                  key={idx}
                  className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-shadow"
                  variants={cardVariants}
                  whileHover="hover"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base flex items-center gap-2">
                      <span className="text-lg">{objective.icon}</span>
                      {objective.title}
                    </h4>
                    <motion.span
                      className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${objective.statusColor}`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {objective.status}
                    </motion.span>
                  </div>

                  <div className="space-y-2 sm:space-y-3">
                    {objective.details.map((detail, detailIdx) => (
                      <motion.div
                        key={detailIdx}
                        className="flex justify-between items-center text-xs sm:text-sm pb-2 border-b border-gray-100"
                        whileHover={{ x: 5 }}
                      >
                        <span className="text-gray-600">{detail.label}</span>
                        <motion.span className="font-semibold text-gray-900" whileHover={{ scale: 1.05 }}>
                          {detail.value}
                        </motion.span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Section - AI Assistant */}
          <motion.div variants={itemVariants} className="lg:col-span-1 flex flex-col gap-6">
            {/* AI Assistant Button */}
            <motion.button
              className="w-full bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-lg p-4 font-medium text-sm flex items-center gap-3 hover:shadow-lg transition-shadow"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span className="text-lg">🤖</span>
              <span className="text-left text-xs">Talk to our AI Assistant, Fundeel</span>
              <motion.span animate={{ y: [0, -2, 0] }} transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}>
                🎤
              </motion.span>
            </motion.button>

            {/* Account Growth Card */}
            <motion.div
              className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-lg p-6 hover:shadow-xl transition-shadow"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium opacity-80">Account Growth</h4>
                <motion.div whileHover={{ rotate: 20 }} className="text-lg">
                  🎯
                </motion.div>
              </div>
              <motion.p className="text-4xl font-bold" whileHover={{ scale: 1.1 }} transition={{ duration: 0.3 }}>
                0%
              </motion.p>

              {/* Divider */}
              <div className="my-6 border-t border-white/10" />

              {/* Countdown Section */}
              <p className="text-xs opacity-75 mb-2">Today's permitted loss will reset in</p>
              <motion.div className="text-center" animate={pulseVariants.animate}>
                <p className="text-3xl font-mono font-bold tracking-wider">{timeLeft}</p>
              </motion.div>
              <p className="text-xs opacity-60 mt-2 text-center">Countdown Timezone GMT+2</p>

              {/* Divider */}
              <div className="my-6 border-t border-white/10" />

              {/* Support */}
              <div className="pt-2">
                <p className="text-xs opacity-75 mb-2 flex items-center gap-2">
                  <span>📧</span>
                  Email Support
                </p>
                <motion.p
                  className="text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity"
                  whileHover={{ scale: 1.05 }}
                >
                  support@fundednext.com
                </motion.p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  )
}
